import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  EyeOff,
  Lock,
  LockOpen,
  PencilLine,
  Printer,
  ShieldCheck,
  Trash2,
  Wallet,
} from 'lucide-react'
import clsx from 'clsx'

import { api, ErrorApi, mensajeDeError } from '@/lib/api'
import { Ulid } from '@/lib/ulid'
import { SelectorCaja } from './SelectorCaja'
import { Modal } from '@/componentes/ui/Modal'
import { Cifra } from '@/componentes/ui/Cifra'
import { fechaHora, hora, plataCentavos } from '@/lib/formato'
import { useSesion } from '@/lib/sesion'
import { useModulos } from '@/lib/modulos'
import { LookupSelect, type ItemLookup } from '@/componentes/ui/LookupSelect'
import { DeclararMerma } from '@/features/merma/DeclararMerma'
import { DialogoCierre } from './DialogoCierre'
import { DialogoAbrirTurno } from './DialogoAbrirTurno'
import { CadenaDeNotas, DialogoCorregirArqueo } from './DialogoCorregirArqueo'
import type { ArqueoTurno, EstadoTurno } from './tipos'

// Turno de caja: abrir, mover plata y cerrar con arqueo.
//
// El turno no es burocracia: es lo que hace que la pregunta «¿cuánto tendría
// que haber en el cajón?» tenga una respuesta. Sin turno no hay arqueo, sin
// arqueo no hay control de caja, y sin control de caja el módulo entero es
// decorativo.
//
// EL ARQUEO ES A CIEGAS. El cajero declara lo que contó SIN ver lo esperado.
// Es la única forma de que el arqueo signifique algo: si ve el número antes de
// contar, escribe ese número y el control no detecta nada. Esta pantalla no
// esconde el dato: el servidor no lo manda (ver ServicioTurnoLocal), y acá se
// explica por qué no está en lugar de dejar un espacio vacío.
export function PanelTurno() {
  const cliente = useQueryClient()
  const { puede } = useSesion()
  const { activo } = useModulos()

  const [abriendo, setAbriendo] = useState(false)
  // El arqueo con el que se abrió el diálogo de cierre, y no un booleano.
  //
  // Es el arreglo de un bug que se veía en el mostrador y no leyendo el
  // código: la condición era `cerrando && arqueo`, y `arqueo` sale de
  // `/turno`, que se invalida al cerrar y vuelve SIN arqueo porque ya no hay
  // turno abierto. Con eso el diálogo se desmontaba en el mismo instante en
  // que iba a mostrar el resultado, así que el cajero cerraba la caja y NO
  // veía si dio o no —justo lo que el arqueo existe para decirle—, y tampoco
  // llegaba al botón de imprimir el cierre ni al de corregirlo. Guardando el
  // arqueo, el diálogo vive hasta que la persona lo cierra.
  const [cerrando, setCerrando] = useState<ArqueoTurno | null>(null)
  const [moviendo, setMoviendo] = useState<'ingreso' | 'egreso' | null>(null)
  const [declarandoMerma, setDeclarandoMerma] = useState(false)
  const [viendoCierre, setViendoCierre] = useState(false)
  const [corrigiendoArqueo, setCorrigiendoArqueo] = useState(false)

  const turno = useQuery({
    queryKey: ['pos', 'turno'],
    queryFn: () => api.get<EstadoTurno>('/turno'),
    refetchInterval: 30_000,
  })

  const refrescar = () => void cliente.invalidateQueries({ queryKey: ['pos', 'turno'] })

  if (turno.isPending) {
    return <p className="p-6 text-[14px] text-tinta-tenue">Cargando el turno…</p>
  }

  const abierto = turno.data?.abierto === true
  const arqueo = turno.data?.arqueo
  const ultimo = abierto ? null : turno.data?.ultimoCerrado

  return (
    // 1400 y no 980: es una pantalla de CONTROL con dos tablas al lado, y a
    // 980 px lo cobrado por medio entraba en tarjetitas de 150 px mientras
    // media pantalla quedaba vacía. El tope sigue estando porque una tabla de
    // cinco columnas estirada a 2560 px deja los importes a un palmo de su
    // etiqueta.
    <div className="mx-auto max-w-[1400px] space-y-4 p-4">
      {/* Encabezado: qué es la pantalla a la izquierda y qué se puede hacer a
          la derecha. Antes las acciones estaban mezcladas con el estado del
          turno y con el selector de caja, así que «Cerrar el turno» —lo que se
          viene a hacer— aparecía en un lugar distinto según si había turno
          abierto o no. */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-[22px] font-bold tracking-[-0.02em] text-tinta">
            <Wallet className="h-6 w-6 text-verde" />
            Caja del turno
          </h1>

          <p className="mt-0.5 text-[13.5px] text-tinta-tenue">
            Controlá el efectivo: apertura, lo que se cobró, lo que se movió del cajón y el cierre.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {abierto && (
            <>
              {/* El comprobante del turno, también con el turno ABIERTO: es el
                  parcial con el que se controla a mitad del día, y el papel
                  mismo aclara que no es el cierre. */}
              <button type="button" onClick={() => setViendoCierre(true)} className="btn-secundario">
                <Printer className="h-4 w-4" /> Comprobante
              </button>

              {/* Los dos botones del cajón viven en el encabezado y no abajo,
                  adentro de su sección: es lo que se viene a hacer con la
                  pantalla abierta, y tenerlos que buscar scrolleando es lo que
                  hace que la plata salga del cajón sin registrarse. */}
              <button
                type="button"
                onClick={() => setMoviendo('ingreso')}
                className="btn-secundario"
              >
                <ArrowDownLeft className="h-4 w-4 text-verde-oscuro" /> Entra plata
              </button>

              <button type="button" onClick={() => setMoviendo('egreso')} className="btn-secundario">
                <ArrowUpRight className="h-4 w-4 text-rojo" /> Sale plata
              </button>

              {puede('caja.cerrar_turno') && (
                <button
                  type="button"
                  onClick={() => arqueo && setCerrando(arqueo)}
                  className="btn-oscuro"
                >
                  <Lock className="h-4 w-4" /> Cerrar la caja
                </button>
              )}
            </>
          )}

          {!abierto && puede('caja.abrir_turno') && (
            <button type="button" onClick={() => setAbriendo(true)} className="btn-primario">
              <LockOpen className="h-4 w-4" /> Abrir la caja
            </button>
          )}
        </div>
      </header>

      {/* En qué caja se está abriendo o cerrando el turno.
          Va acá y no solo en la pantalla de venta porque el turno es LO MÁS
          específico de la caja: abrirlo en la caja equivocada deja al otro
          mostrador sin poder cobrar y mezcla la plata de los dos. */}
      <SelectorCaja />

      {/* La banda de estado. Es lo primero que se mira al entrar y hasta acá
          estaba como un renglón gris abajo del título, que se lee como una
          aclaración y no como el estado de la caja. */}
      <BandaEstado
        abierta={abierto}
        desde={arqueo?.abiertoAt}
        quien={turno.data?.usuarioNombre}
        puedeAbrir={puede('caja.abrir_turno')}
      />

      {abierto && arqueo && (
        <>
          <CifrasDelCajon arqueo={arqueo} />

          {/* Lo cobrado y lo que se movió del cajón, UNO AL LADO DEL OTRO en
              pantalla ancha. Son las dos mitades de la misma pregunta —de
              dónde salió la plata que tendría que estar en el cajón— y
              apiladas obligaban a scrollear entre una y otra justo mientras
              se arquea. Lo cobrado ocupa menos, así que se lleva la columna
              angosta. */}
          <div className="grid gap-4 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
            <CobradoPorMedio arqueo={arqueo} />

            <MovimientosDelCajon
              arqueo={arqueo}
              puedeMover={puede('caja.movimiento')}
              onEntra={() => setMoviendo('ingreso')}
              onSale={() => setMoviendo('egreso')}
            />
          </div>
        </>
      )}

      {/* El último turno que cerró, con su arqueo y la posibilidad de
          corregirlo.

          Es la mitad que faltaba de la corrección: el diálogo del cierre la
          ofrece en el momento, pero el cajero se acuerda de que quedó plata en
          el otro cajón cinco minutos después de apretar «Listo», y hasta acá
          esta pantalla decía «No hay ningún turno abierto» y nada más. Una
          función que existe y no se puede alcanzar es el mismo error que la
          tecla rápida del medio de pago.

          No caduca: si el faltante se explica al otro día, el que audita
          prefiere leer la explicación tarde que no leerla. Lo que hace que se
          entienda es que la nota dice cuánto DESPUÉS del cierre se escribió. */}
      {ultimo && puede('caja.cerrar_turno') && (
        <section className="carta overflow-hidden">
          <h2 className="border-b border-borde px-4 py-3 text-[14px] font-semibold text-tinta">
            La última caja que cerró
          </h2>

          <div className="space-y-3 px-4 py-3">
            <p className="text-[13px] text-tinta-tenue">Cerrada {fechaHora(ultimo.cerradoAt)}</p>

            {ultimo.diferenciaCentavos == null ? (
              <p className="text-[13.5px] leading-relaxed text-tinta-suave">
                Se declararon{' '}
                <span className="num font-semibold">
                  {plataCentavos(ultimo.declaradoCentavos ?? 0)}
                </span>
                . La diferencia la revisa el encargado.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Cifra
                  titulo="Esperado"
                  valor={plataCentavos(ultimo.esperadoEnCajonCentavos ?? 0)}
                />
                <Cifra titulo="Contado" valor={plataCentavos(ultimo.declaradoCentavos ?? 0)} />
                <Cifra
                  titulo={
                    ultimo.diferenciaCentavos === 0
                      ? 'Cerró justo'
                      : ultimo.diferenciaCentavos > 0
                        ? 'Sobró'
                        : 'Faltó'
                  }
                  valor={plataCentavos(Math.abs(ultimo.diferenciaCentavos))}
                  alerta={ultimo.diferenciaCentavos !== 0}
                />
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCorrigiendoArqueo(true)}
                className="btn-secundario"
              >
                <PencilLine className="h-4 w-4" /> Corregir el arqueo
              </button>

              <button type="button" onClick={() => setViendoCierre(true)} className="btn-secundario">
                <Printer className="h-4 w-4" /> Ver el comprobante
              </button>
            </div>
          </div>

          {(ultimo.notas ?? []).length > 0 && (
            <>
              <h3 className="border-y border-borde bg-crema-alt px-4 py-2 text-[13px] font-semibold text-tinta">
                Lo que se anotó después de cerrar
              </h3>

              <CadenaDeNotas notas={ultimo.notas ?? []} cerradoAt={ultimo.cerradoAt} />
            </>
          )}
        </section>
      )}

      {corrigiendoArqueo && ultimo && (
        <DialogoCorregirArqueo
          turnoId={ultimo.turnoId}
          declaradoCentavos={ultimo.declaradoCentavos}
          esperadoCentavos={ultimo.esperadoEnCajonCentavos}
          onCerrar={() => setCorrigiendoArqueo(false)}
          onListo={refrescar}
        />
      )}

      {/* Declarar merma NO está dentro del bloque del turno abierto: la
          mercadería se revisa a la mañana, antes de abrir la caja, que es
          justo cuando se ve lo que no aguantó la noche.

          Y no aparece si el local apagó el módulo: el endpoint contesta 404,
          así que el botón sería una promesa que el servidor no cumple. */}
      {puede('merma.declarar') && activo('merma') && (
        <section className="carta flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="min-w-0">
            <h2 className="text-[14px] font-semibold text-tinta">Merma</h2>

            <p className="mt-0.5 text-[13px] leading-relaxed text-tinta-tenue">
              Lo que se tiró, se regaló o se remató. Se declara desde acá porque acá es donde se
              ve el cajón: si hay que esperar a que alguien la cargue de la computadora, no se
              carga.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setDeclarandoMerma(true)}
            className="btn-secundario shrink-0"
          >
            <Trash2 className="h-4 w-4 text-rojo" /> Declarar merma
          </button>
        </section>
      )}

      {abriendo && <DialogoAbrirTurno onCerrar={() => setAbriendo(false)} onListo={refrescar} />}

      {cerrando && (
        <DialogoCerrar arqueo={cerrando} onCerrar={() => setCerrando(null)} onListo={refrescar} />
      )}

      {moviendo && (
        <DialogoMovimiento
          tipo={moviendo}
          onCerrar={() => setMoviendo(null)}
          onListo={refrescar}
        />
      )}

      {declarandoMerma && (
        <DeclararMerma
          onCerrar={() => setDeclarandoMerma(false)}
          onDeclarada={() => setDeclarandoMerma(false)}
        />
      )}

      {viendoCierre && <DialogoCierre onCerrar={() => setViendoCierre(false)} />}
    </div>
  )
}

// ── Las piezas de la pantalla ───────────────────────────────────────────────

/**
 * Si la caja está abierta o cerrada, desde cuándo y por quién.
 *
 * Es lo primero que se mira al entrar, y estaba como un renglón gris abajo del
 * título: se leía como una aclaración del encabezado y no como el estado de la
 * caja. Con la banda, entrar y saber en qué está la caja es una mirada.
 */
function BandaEstado({
  abierta,
  desde,
  quien,
  puedeAbrir,
}: {
  abierta: boolean
  desde?: string
  quien?: string
  puedeAbrir: boolean
}) {
  if (!abierta) {
    return (
      <p
        className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-borde
                   border-l-4 border-l-borde-fuerte bg-papel px-4 py-3 text-[14px] text-tinta-suave"
      >
        <Lock className="h-4 w-4 shrink-0 text-tinta-tenue" />
        <strong className="font-semibold text-tinta">La caja está cerrada.</strong>
        <span className="text-tinta-tenue">
          {puedeAbrir
            ? 'Abrila para poder cobrar y registrar movimientos.'
            : 'La abre alguien con permiso para hacerlo.'}
        </span>
      </p>
    )
  }

  return (
    <p
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-verde/30
                 border-l-4 border-l-verde bg-verde-tenue px-4 py-3 text-[14px]"
    >
      <LockOpen className="h-4 w-4 shrink-0 text-verde-oscuro" />
      <strong className="font-semibold text-verde-oscuro">Caja abierta</strong>
      <span className="text-tinta-suave">
        · desde {fechaHora(desde)}
        {quien ? ` por ${quien}` : ''}
      </span>
    </p>
  )
}

/**
 * Las cuatro cifras del cajón: con qué arrancó, qué entró, qué salió y cuánto
 * tendría que haber.
 *
 * El «esperado» es el número del arqueo A CIEGAS, así que puede no venir: el
 * servidor no lo manda mientras el cajero no declaró lo que contó. Acá se
 * explica por qué no está en lugar de dejar la tarjeta vacía, que se lee como
 * un sistema que no calculó.
 *
 * Lo que entró y lo que salió van SEPARADOS y no como neto, por lo mismo que
 * el faltante y el sobrante del Inicio: «−$1.200» puede ser un retiro de
 * $1.200 o una reposición de $10.000 con un retiro de $11.200, y son dos
 * turnos distintos.
 */
function CifrasDelCajon({ arqueo }: { arqueo: ArqueoTurno }) {
  // Un host viejo no manda el desglose (el ejecutable se actualiza por OTA y
  // puede quedar un rato atrás): ahí se cae al neto, que es lo que había
  // antes, en lugar de mostrar dos ceros que serían mentira.
  const desglosado = arqueo.ingresosCentavos !== undefined && arqueo.egresosCentavos !== undefined

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Cifra titulo="Fondo inicial" valor={plataCentavos(arqueo.fondoInicialCentavos)} />

      {desglosado ? (
        <>
          <Cifra
            titulo="Entró al cajón"
            valor={plataCentavos(arqueo.ingresosCentavos ?? 0)}
            detalle="Reposición de cambio, cobranzas"
          />

          <Cifra
            titulo="Salió del cajón"
            valor={plataCentavos(arqueo.egresosCentavos ?? 0)}
            detalle="Retiros, pagos, fletes"
          />
        </>
      ) : (
        <Cifra
          titulo="Neto del cajón"
          valor={plataCentavos(arqueo.movimientosCentavos)}
          detalle="Ingresos menos egresos"
        />
      )}

      {arqueo.esperadoEnCajonCentavos != null ? (
        <Cifra
          titulo="Esperado en el cajón"
          valor={plataCentavos(arqueo.esperadoEnCajonCentavos)}
          detalle="Contra esto se compara el arqueo"
        />
      ) : (
        <div className="carta flex flex-col justify-center px-3 py-2.5 sm:px-4 sm:py-3">
          <p className="flex items-center gap-1.5 text-[12.5px] font-medium text-tinta-tenue">
            <EyeOff className="h-3.5 w-3.5" /> Esperado en el cajón
          </p>

          <p className="mt-1 text-[12.5px] leading-snug text-tinta-tenue">
            No se muestra hasta que declares lo contado. Es a propósito: si se viera antes, el
            arqueo no detectaría ninguna diferencia.
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Lo cobrado en el turno: el total y cuánto entró por cada medio de pago.
 *
 * Faltaba de verdad, y era lo único que la pantalla tenía del cobro: una lista
 * de renglones con el nombre y el importe, sin total y sin decir cuántos pagos
 * fueron. «$180.000 en tarjeta» no dice si fueron dos compras grandes o
 * cuarenta chicas, y esa es la diferencia entre un día normal y uno que hay
 * que mirar.
 *
 * Los medios van TODOS a la vista y no en solapas: la pregunta de esta
 * pantalla es con qué se cobró el turno, o sea la comparación entre ellos, y
 * una solapa por medio esconde justo lo que se vino a comparar. Son tres o
 * cuatro en una verdulería, así que entran en una fila.
 */
function CobradoPorMedio({ arqueo }: { arqueo: ArqueoTurno }) {
  const total = arqueo.porMedio.reduce((a, m) => a + m.totalCentavos, 0)

  return (
    <section className="carta overflow-hidden">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-borde px-4 py-3">
        <h2 className="text-[14px] font-semibold text-tinta">
          {arqueo.aCiegas ? 'Cobrado, sin el efectivo' : 'Cobrado en el turno'}
        </h2>

        <p className="text-[12.5px] text-tinta-tenue">
          {arqueo.ventasCount} {arqueo.ventasCount === 1 ? 'venta' : 'ventas'}
          {arqueo.ventasAnuladasCount > 0 && (
            // Se muestra siempre que haya alguna: es el número que se mira
            // para detectar fuga de efectivo, porque cobrar y después anular
            // es la forma clásica.
            <span className="text-ambar">
              {' '}
              · {arqueo.ventasAnuladasCount}{' '}
              {arqueo.ventasAnuladasCount === 1 ? 'anulada' : 'anuladas'}
            </span>
          )}
        </p>
      </header>

      {arqueo.porMedio.length === 0 ? (
        <p className="px-4 py-6 text-center text-[13.5px] text-tinta-tenue">
          {arqueo.aCiegas
            ? 'Todavía no se cobró nada con los medios que no son efectivo.'
            : 'Todavía no se cobró nada en este turno.'}
        </p>
      ) : (
        // Es una TABLA y no tarjetas, y es la diferencia entre mirar y
        // comparar: la pregunta de esta sección es con qué se cobró el turno,
        // o sea la relación entre los medios, y eso se lee en una columna de
        // importes alineados y no en cuatro cuadraditos de 150 px. Además el
        // renglón del total cierra la cuenta a la vista, que es lo que el que
        // arquea está haciendo.
        <div className="overflow-x-auto">
          <table className="tabla">
            <thead>
              <tr>
                <th>Medio de pago</th>
                <th className="num text-right">Pagos</th>
                <th className="num text-right">Importe</th>
                <th className="num text-right">Part.</th>
              </tr>
            </thead>

            <tbody>
              {arqueo.porMedio.map((m) => (
                <tr key={m.medioPagoIdNube}>
                  <td>
                    <span className="flex items-center gap-1.5">
                      {/* El punto verde marca los que SUMAN AL CAJÓN, que son
                          los que hay que contar al arquear. Sin la marca, el
                          que cuenta tiene que acordarse de cuáles son. */}
                      {m.sumaAlCajon && (
                        <span
                          aria-hidden
                          title="Suma al cajón"
                          className="h-2 w-2 shrink-0 rounded-full bg-verde"
                        />
                      )}
                      <span className="font-medium text-tinta">{m.medioPagoNombre}</span>
                    </span>
                  </td>

                  {/* La cantidad de pagos puede no venir de un host viejo, y
                      ahí va un guión: «0 pagos» sería mentira justo al lado de
                      un importe que no es cero. */}
                  <td className="num text-right text-tinta-suave">{m.pagosCount ?? '—'}</td>

                  <td className="num text-right font-medium text-tinta">
                    {plataCentavos(m.totalCentavos)}
                  </td>

                  <td className="num text-right text-tinta-tenue">
                    {total > 0 ? `${Math.round((m.totalCentavos / total) * 100)}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr className="border-t-2 border-borde-fuerte">
                <td className="font-semibold text-tinta">Total</td>

                <td className="num text-right text-tinta-suave">
                  {arqueo.porMedio.every((m) => m.pagosCount !== undefined)
                    ? arqueo.porMedio.reduce((a, m) => a + (m.pagosCount ?? 0), 0)
                    : '—'}
                </td>

                <td className="num text-right text-[15px] font-bold text-tinta">
                  {plataCentavos(total)}
                </td>

                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {arqueo.aCiegas && (
        <p className="flex items-start gap-2 border-t border-borde bg-crema-alt px-4 py-3 text-[12.5px] text-tinta-tenue">
          <EyeOff className="mt-0.5 h-4 w-4 shrink-0" />
          El efectivo cobrado no se muestra hasta que se declare lo contado, igual que el
          esperado: con el fondo y los movimientos a la vista, el efectivo alcanzaría para
          despejar el esperado con una resta.
        </p>
      )}
    </section>
  )
}

/**
 * Los movimientos del cajón del turno, uno por uno.
 *
 * Es lo que más faltaba: la pantalla mostraba un NETO y nada más, así que el
 * cajero que arqueaba veía «salieron $4.500» sin poder decir de qué, y el
 * encargado que venía a mirar tenía que irse a Control de caja —que pide
 * `reporte.ver`, o sea justo el permiso que el cajero no tiene—. Un arqueo que
 * no deja ver qué movió la plata obliga a aceptarlo o a discutirlo sin datos.
 *
 * Es una `<table className="tabla">` y no un DataGrid a propósito (§1.3): son
 * los pocos renglones de UN turno y se leen de arriba a abajo, como el
 * extracto de una cuenta o el detalle de una venta.
 */
function MovimientosDelCajon({
  arqueo,
  puedeMover,
  onEntra,
  onSale,
}: {
  arqueo: ArqueoTurno
  puedeMover: boolean
  onEntra: () => void
  onSale: () => void
}) {
  const movimientos = arqueo.movimientos

  return (
    <section className="carta overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-borde px-4 py-3">
        <h2 className="text-[14px] font-semibold text-tinta">
          Movimientos del cajón
          {movimientos !== undefined && (
            <span className="ml-1.5 font-normal text-tinta-tenue">({movimientos.length})</span>
          )}
        </h2>

        {/* El aviso va SIEMPRE que falte el permiso, y los botones del
            encabezado se muestran igual: con el botón escondido, el cajero al
            que le falta `caja.movimiento` no tiene cómo pedirle al encargado
            que lo autorice, y lo único que le queda es sacar la plata del
            cajón sin registrarla —que es justo lo que este registro existe
            para evitar—. El servidor decide igual. */}
        {!puedeMover && (
          <p className="flex items-center gap-1.5 text-[12.5px] text-ambar">
            <ShieldCheck className="h-3.5 w-3.5" />
            Un encargado tiene que autorizarlo con su PIN.
          </p>
        )}
      </header>

      {movimientos === undefined ? (
        // Host viejo: manda el neto y no la lista. Se dice el neto en lugar de
        // mostrar una tabla vacía, que se leería como «no hubo movimientos».
        <p className="px-4 py-6 text-center text-[13.5px] text-tinta-tenue">
          Neto de los movimientos:{' '}
          <span
            className={clsx(
              'num font-semibold',
              arqueo.movimientosCentavos < 0 ? 'text-rojo' : 'text-verde-oscuro',
            )}
          >
            {plataCentavos(arqueo.movimientosCentavos)}
          </span>
        </p>
      ) : movimientos.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <p className="text-[13.5px] text-tinta-tenue">
            Todavía no se movió plata del cajón en este turno.
          </p>

          <p className="mx-auto mt-1 max-w-[52ch] text-[12.5px] leading-relaxed text-tinta-tenue">
            Reposición de cambio, retiro para el banco, el flete que se pagó de la caja. Sin
            registrarlos, el arqueo no cierra nunca y el cajero aprende a ignorar la diferencia.
          </p>

          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <button type="button" onClick={onEntra} className="btn-secundario">
              <ArrowDownLeft className="h-4 w-4 text-verde-oscuro" /> Entra plata
            </button>

            <button type="button" onClick={onSale} className="btn-secundario">
              <ArrowUpRight className="h-4 w-4 text-rojo" /> Sale plata
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="tabla">
            <thead>
              <tr>
                <th>Hora</th>
                <th>Concepto</th>
                <th>Quién</th>
                <th className="num text-right">Entró</th>
                <th className="num text-right">Salió</th>
              </tr>
            </thead>

            <tbody>
              {movimientos.map((m) => (
                <tr key={m.id}>
                  <td className="num whitespace-nowrap text-tinta-tenue">{hora(m.fechaAt)}</td>

                  <td>
                    <span className="font-medium text-tinta">{m.concepto ?? 'Sin concepto'}</span>

                    {m.detalle && (
                      <span className="block text-[12.5px] text-tinta-tenue">{m.detalle}</span>
                    )}
                  </td>

                  <td className="text-[13px] text-tinta-suave">
                    {m.usuario ?? '—'}

                    {/* Quién AUTORIZÓ va al lado y no en lugar del cajero: el
                        movimiento es de quien lo registró y la autorización es
                        de otro, y mezclarlos borra el dato que se audita. */}
                    {m.autorizadoPor && (
                      <span className="block text-[12px] text-ambar">
                        Autorizó {m.autorizadoPor}
                      </span>
                    )}
                  </td>

                  <td className="num text-right font-medium text-verde-oscuro">
                    {m.esIngreso ? plataCentavos(m.importeCentavos) : ''}
                  </td>

                  <td className="num text-right font-medium text-rojo">
                    {m.esIngreso ? '' : plataCentavos(m.importeCentavos)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

// ── Cerrar (arqueo a ciegas) ────────────────────────────────────────────────

function DialogoCerrar({
  arqueo,
  onCerrar,
  onListo,
}: {
  arqueo: ArqueoTurno
  onCerrar: () => void
  onListo: () => void
}) {
  const [pesos, setPesos] = useState('')
  const [observaciones, setObservaciones] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [resultado, setResultado] = useState<ArqueoTurno | null>(null)
  const [viendoComprobante, setViendoComprobante] = useState(false)
  const [corrigiendo, setCorrigiendo] = useState(false)

  const cerrar = useMutation({
    mutationFn: () =>
      api.post<ArqueoTurno>('/turno/cerrar', {
        declaradoCentavos: Math.round(Number(pesos || 0) * 100),
        observaciones: observaciones.trim() || undefined,
      }),

    onSuccess: (r) => {
      setResultado(r)
      onListo()
    },

    onError: (e) =>
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cerrar el turno. Reintentá.'),
  })

  // Ya cerrado: se muestra el resultado. La diferencia aparece solo si quien
  // cerró tiene el permiso de verla; si no, el servidor la manda en null y
  // acá se dice que el turno cerró y nada más.
  if (resultado) {
    const dif = resultado.diferenciaCentavos
    const notas = resultado.notas ?? []

    return (
      <Modal
        onCerrar={onCerrar}
        titulo="Turno cerrado"
        ancho="sm"
        // El pie va en el `pie` del Modal y no adentro del cuerpo, que es
        // donde estaba: el Modal se acota al alto de la ventana y reparte por
        // flex, así que un botón armado en el cuerpo queda abajo del pliegue
        // en un teléfono. Es el gotcha que el sistema ya pagó cuatro veces.
        pie={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setViendoComprobante(true)}
              className="btn-secundario flex-1"
            >
              <Printer className="h-4 w-4" /> Imprimir
            </button>

            <button type="button" onClick={onCerrar} className="btn-primario flex-1">
              Listo
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          {dif == null ? (
            <p className="text-[14px] leading-relaxed text-tinta-suave">
              El turno quedó cerrado con {plataCentavos(resultado.declaradoCentavos ?? 0)}{' '}
              declarados. La diferencia la revisa el encargado.
            </p>
          ) : (
            <>
              <Renglon etiqueta="Esperado" valor={resultado.esperadoEnCajonCentavos ?? 0} />
              <Renglon etiqueta="Declarado" valor={resultado.declaradoCentavos ?? 0} />

              <div className="border-t border-borde pt-3">
                <Renglon
                  etiqueta={dif === 0 ? 'Cerró justo' : dif > 0 ? 'Sobró' : 'Faltó'}
                  valor={Math.abs(dif)}
                  fuerte
                  acento={dif === 0 ? 'verde' : 'rojo'}
                />
              </div>
            </>
          )}

          {/* Corregir el arqueo, ACÁ y no en otra pantalla.
              Este es el segundo en que el cajero descubre que el número no da:
              tipeó 2.000 donde iba 20.000, o se acuerda de que quedó plata en
              el otro cajón. Sin el botón en este momento, lo que le queda es
              nada —el turno ya está cerrado y el movimiento de caja exige uno
              abierto—, y la diferencia se archiva sin nombre.

              No pide el PIN de un encargado a propósito: pedirle a otro que
              autorice una EXPLICACIÓN es garantizar que nadie la escriba. Lo
              anterior no se pisa, se suma con quién y cuándo. */}
          <div className="rounded-lg bg-crema-alt px-3 py-3">
            <p className="text-[12.5px] leading-relaxed text-tinta-suave">
              {dif != null && dif !== 0
                ? 'Si contaste mal o te acordás de algo, se puede anotar. Queda con tu nombre '
                  + 'y no borra lo que declaraste.'
                : 'Si después aparece una diferencia, se puede anotar acá mismo. Queda con tu '
                  + 'nombre y no borra lo que declaraste.'}
            </p>

            <button
              type="button"
              onClick={() => setCorrigiendo(true)}
              className="btn-secundario btn-sm mt-2"
            >
              <PencilLine className="h-4 w-4" /> Corregir el arqueo
            </button>
          </div>

          {notas.length > 0 && (
            <section className="overflow-hidden rounded-lg border border-borde">
              <h3 className="border-b border-borde bg-crema-alt px-4 py-2 text-[13px] font-semibold text-tinta">
                Lo que se anotó
              </h3>

              <CadenaDeNotas notas={notas} cerradoAt={resultado.cerradoAt} />
            </section>
          )}

          {viendoComprobante && (
            <DialogoCierre
              turnoId={resultado.turnoId}
              onCerrar={() => setViendoComprobante(false)}
            />
          )}

          {corrigiendo && (
            <DialogoCorregirArqueo
              turnoId={resultado.turnoId}
              declaradoCentavos={resultado.declaradoCentavos}
              esperadoCentavos={resultado.esperadoEnCajonCentavos}
              onCerrar={() => setCorrigiendo(false)}
              // El arqueo que devuelve el servidor reemplaza al de la
              // pantalla: trae la nota nueva y el declarado que vale ahora,
              // así que la lista se ve al instante sin volver a consultar.
              onListo={(nuevo) => {
                setResultado(nuevo)
                onListo()
              }}
            />
          )}
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      onCerrar={onCerrar}
      titulo="Cerrar el turno"
      descripcion="Contá el efectivo del cajón y escribí lo que contaste."
      ancho="sm"
    >
      <div className="space-y-4">
        {/* Se avisa que el arqueo va a ciegas ANTES de pedir el número, no
            después: el cajero tiene que saber que no va a poder comparar. */}
        <p className="flex items-start gap-2 rounded-lg bg-crema-alt px-3 py-2.5 text-[12.5px] text-tinta-suave">
          <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-tinta-tenue" />
          No te mostramos cuánto tendría que haber. Es para que el conteo sirva de control: contá
          primero y escribí ese número.
        </p>

        <div>
          <label
            htmlFor="declarado"
            className="mb-1.5 block text-[13px] font-medium text-tinta-suave"
          >
            Efectivo contado
          </label>

          <input
            id="declarado"
            inputMode="numeric"
            value={pesos}
            onChange={(e) => setPesos(e.target.value.replace(/[^\d]/g, ''))}
            placeholder="0"
            autoFocus
            className="num w-full rounded-lg border border-borde bg-papel px-3 py-3 text-right
                       text-[22px] outline-none focus:border-verde"
          />
        </div>

        <div>
          <label
            htmlFor="obs"
            className="mb-1.5 block text-[13px] font-medium text-tinta-suave"
          >
            Observaciones <span className="font-normal text-tinta-tenue">(opcional)</span>
          </label>

          <textarea
            id="obs"
            rows={2}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Le di mal el vuelto a un cliente, quedó anotado"
            className="w-full resize-none rounded-lg border border-borde bg-papel px-3 py-2.5
                       text-[14px] outline-none focus:border-verde"
          />
        </div>

        {arqueo.ventasAnuladasCount > 0 && (
          <p className="text-[12.5px] text-ambar">
            Este turno tiene {arqueo.ventasAnuladasCount} venta
            {arqueo.ventasAnuladasCount === 1 ? '' : 's'} anulada
            {arqueo.ventasAnuladasCount === 1 ? '' : 's'}.
          </p>
        )}

        {error && (
          <p className="flex items-start gap-2 rounded-lg bg-rojo/10 px-3 py-2.5 text-[13px] text-rojo">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <button type="button" onClick={onCerrar} className="btn-secundario flex-1">
            Cancelar
          </button>

          <button
            type="button"
            onClick={() => cerrar.mutate()}
            disabled={cerrar.isPending || pesos === ''}
            className="btn-primario flex-1"
          >
            {cerrar.isPending ? 'Cerrando…' : 'Cerrar'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ── Movimiento de caja ──────────────────────────────────────────────────────

function DialogoMovimiento({
  tipo,
  onCerrar,
  onListo,
}: {
  tipo: 'ingreso' | 'egreso'
  onCerrar: () => void
  onListo: () => void
}) {
  const [pesos, setPesos] = useState('')
  const [concepto, setConcepto] = useState<ItemLookup | null>(null)
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  // El ULID se genera UNA vez, al abrir el diálogo, y no al mandar: es la
  // clave de idempotencia y generarla al mandar la hace decorativa justo en el
  // caso para el que existe. Sin esto, el doble click en Registrar metía DOS
  // movimientos en el cajón, y el arqueo quedaba mal por ese importe sin que
  // nada lo dijera. La flecha va explícita porque `useState(Ulid.nuevo)` lo
  // rechaza el linter (unbound-method).
  const [ulid] = useState(() => Ulid.nuevo())

  const registrar = useMutation({
    mutationFn: () => {
      const centavos = Math.round(Number(pesos || 0) * 100)

      return api.post('/turno/movimiento', {
        ulid,

        // El signo lo define el tipo, no el número que se tipeó: pedirle al
        // cajero que escriba un negativo es pedirle un error.
        importeCentavos: tipo === 'egreso' ? -centavos : centavos,

        // El concepto sale del catálogo del local, no de un literal: es lo que
        // permite que el reporte de egresos agrupe («flete», «pago a
        // proveedor», «retiro del dueño»), que es de lo único que sirve. El
        // nombre viaja congelado además del id, para que un concepto dado de
        // baja no deje al movimiento sin explicación.
        tipoId: concepto?.id ?? 0,
        tipoNombre: concepto?.nombre ?? '',
        motivo: motivo.trim(),
      })
    },

    onSuccess: () => {
      onListo()
      onCerrar()
    },

    // `mensajeDeError` y no `.message`: el 400 de validación manda un genérico
    // («Hay errores de validación.») y el motivo real por campo, y acá hay un
    // solo renglón de error.
    onError: (e) => setError(mensajeDeError(e, 'No se pudo registrar el movimiento.')),
  })

  return (
    <Modal
      onCerrar={onCerrar}
      titulo={tipo === 'egreso' ? 'Sale plata del cajón' : 'Entra plata al cajón'}
      ancho="sm"
      // Los botones van en el PIE y no en el cuerpo. Estaban adentro, que es
      // el gotcha del pie abajo del pliegue otra vez: en un teléfono, con el
      // importe, el concepto y el detalle apilados, «Registrar» quedaba abajo
      // del borde de la pantalla y el diálogo nativo no scrollea. El `pie` del
      // Modal no scrollea nunca.
      pie={
        <>
          <button type="button" onClick={onCerrar} className="btn-secundario">
            Cancelar
          </button>

          <button
            type="button"
            onClick={() => registrar.mutate()}
            disabled={registrar.isPending || pesos === '' || !concepto}
            className="btn-primario"
          >
            {registrar.isPending ? 'Registrando…' : 'Registrar'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="importe-mov"
            className="mb-1.5 block text-[13px] font-medium text-tinta-suave"
          >
            Importe
          </label>

          <input
            id="importe-mov"
            inputMode="numeric"
            value={pesos}
            onChange={(e) => setPesos(e.target.value.replace(/[^\d]/g, ''))}
            placeholder="0"
            autoFocus
            className="num w-full rounded-lg border border-borde bg-papel px-3 py-3 text-right
                       text-[22px] outline-none focus:border-verde"
          />
        </div>

        <LookupSelect
          fuente="catalogo/CONCEPTO_CAJA"
          etiqueta="Concepto"
          valor={concepto?.id ?? null}
          onCambio={(_, item) => {
            setConcepto(item)
            setError(null)
          }}
          placeholder={tipo === 'egreso' ? 'Retiro para el banco, flete…' : 'Reposición de cambio…'}
          requerido
        />

        <div>
          <label
            htmlFor="motivo-mov"
            className="mb-1.5 block text-[13px] font-medium text-tinta-suave"
          >
            Detalle (opcional)
          </label>

          <input
            id="motivo-mov"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Lo que haga falta aclarar."
            className="w-full rounded-lg border border-borde bg-papel px-3 py-2.5 text-[14px]
                       outline-none focus:border-verde"
          />

          <p className="mt-1.5 text-[12.5px] text-tinta-tenue">
            El concepto alcanza para el reporte. El detalle es para lo que el concepto no dice: a
            quién se le pagó, de qué factura.
          </p>
        </div>

        {error && (
          <p className="flex items-start gap-2 rounded-lg bg-rojo/10 px-3 py-2.5 text-[13px] text-rojo">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}

// ── Piezas ──────────────────────────────────────────────────────────────────

function Renglon({
  etiqueta,
  valor,
  fuerte,
  acento,
}: {
  etiqueta: string
  valor: number
  fuerte?: boolean
  acento?: 'rojo' | 'verde'
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[13.5px] text-tinta-suave">{etiqueta}</span>

      <span
        className={clsx(
          'num',
          fuerte ? 'text-[22px] font-bold' : 'text-[15px]',
          acento === 'rojo' ? 'text-rojo' : acento === 'verde' ? 'text-verde-oscuro' : 'text-tinta',
        )}
      >
        {plataCentavos(valor)}
      </span>
    </div>
  )
}
